import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import SideNav from "../SideNav";
import SherDineNav from "../SherDineNav";
import { DBContext } from "../DBContext";
import BillDesignRenderer from './BillDesignRenderer';

const dynamicFields = [
	{ type: 'invoice_number', label: 'Invoice Number', placeholder: 'Invoice No: {{invoiceNumber}}' },
	{ type: 'table_number', label: 'Table Number', placeholder: 'Table: {{tableNumber}}' },
	{ type: 'customer_name', label: 'Customer Name', placeholder: 'Name: {{customerName}}' },
	{ type: 'date_time', label: 'Date & Time', placeholder: 'Date: {{date}} Time: {{time}}' },
	{ type: 'cashier', label: 'Cashier Name', placeholder: 'Cashier: {{cashier}}' },
	{ type: 'waiter', label: 'Waiter Name', placeholder: 'Waiter: {{waiter}}' },
	{ type: 'subtotal', label: 'Sub-Total', placeholder: 'Sub-Total: {{subtotal}}' },
	{ type: 'cgst', label: 'CGST Amount', placeholder: 'CGST: {{cgst}}' },
	{ type: 'sgst', label: 'SGST Amount', placeholder: 'SGST: {{sgst}}' },
	{ type: 'total', label: 'Grand Total', placeholder: 'Total: {{grandTotal}}' },
	{ type: 'items_table', label: 'Items Table', placeholder: '' },
	{ type: 'gst_summary', label: 'GST Summary', placeholder: '' },
];

// Predefined paper sizes (in mm, converted to pixels at 96 DPI)
const PAPER_SIZES = [
	{ name: "Thermal Paper (58x297mm)", width: 58, height: 297, description: "Standard thermal paper" },
	{ name: "A4 (210x297mm)", width: 210, height: 297, description: "Standard A4 paper" },
	{ name: "A5 (148x210mm)", width: 148, height: 210, description: "A5 paper" },
	{ name: "Letter (216x279mm)", width: 216, height: 279, description: "US Letter" },
	{ name: "Half Letter (140x216mm)", width: 140, height: 216, description: "Half letter size" },
	{ name: "Receipt (80x297mm)", width: 80, height: 297, description: "Standard receipt paper" },
	{ name: "Custom", width: null, height: null, description: "Set custom dimensions" },
];

// Convert mm to pixels at 96 DPI (1 inch = 25.4mm, so 1mm = 96/25.4 ≈ 3.78px)
const mmToPixels = (mm) => Math.round(mm * (96 / 25.4));
const pixelsToMM = (px) => Math.round(px / (96 / 25.4));

const BillDesigner = ({ onSaveDesign, initialDesign }) => {
	const navigate = useNavigate();
	useEffect(() => {
		if (localStorage.getItem('USER') !== 'shersoft') {
			navigate('/ViewDashboard');
		}
	}, [navigate]);

	const { logginedDB, companyDetails } = useContext(DBContext);
	const [elements, setElements] = useState(initialDesign?.elements || []);
	const [selectedElement, setSelectedElement] = useState(null);
	const [previewMode, setPreviewMode] = useState(false);
	const [backgroundColor, setBackgroundColor] = useState(initialDesign?.backgroundColor || "#ffffff");
	const [canvasSize, setCanvasSize] = useState(initialDesign?.canvasSize || { width: mmToPixels(58), height: mmToPixels(297) });
	const [sideNav, setSideNav] = useState(false);
	const [falseState, setFalseState] = useState(null);
	const [mobileToolbarOpen, setMobileToolbarOpen] = useState(false);

	// New state for zoom
	const [zoomLevel, setZoomLevel] = useState(100); // 100% zoom by default
	const [selectedPaperSize, setSelectedPaperSize] = useState("Thermal Paper (58x297mm)");

	const handleSideNav = (state) => {
		setSideNav(state);
	};

	const [collapsedSections, setCollapsedSections] = useState({
		elementProperties: false,
		companyData: false,
		dynamicFields: false,
		quickActions: false
	});

	// Add grid/rulers state
	const [showGrid, setShowGrid] = useState(true);
	const [showRulers, setShowRulers] = useState(true);

	// Add toggle function
	const toggleSection = (section) => {
		setCollapsedSections(prev => ({
			...prev,
			[section]: !prev[section]
		}));
	};

	const handleImportDesign = () => {
		// Create a file input element
		const fileInput = document.createElement('input');
		fileInput.type = 'file';
		fileInput.accept = '.json';

		fileInput.onchange = (e) => {
			const file = e.target.files[0];
			if (!file) return;

			const reader = new FileReader();
			reader.onload = (event) => {
				try {
					const design = JSON.parse(event.target.result);

					// Validate the design structure
					if (!design.elements || !design.canvasSize) {
						throw new Error('Invalid design file format');
					}

					// Ask for confirmation before overwriting
					if (window.confirm("Load this design? Current elements will be replaced.")) {
						// Set the imported design
						setElements(design.elements || []);
						setBackgroundColor(design.backgroundColor || "#ffffff");
						setCanvasSize(design.canvasSize || { width: mmToPixels(58), height: mmToPixels(297) });
						setSelectedElement(null);

						// Determine paper size from imported dimensions
						const importedWidthMM = pixelsToMM(design.canvasSize.width);
						const importedHeightMM = pixelsToMM(design.canvasSize.height);

						// Try to find matching paper size
						const matchedPaper = PAPER_SIZES.find(paper =>
							paper.width === importedWidthMM && paper.height === importedHeightMM
						);

						if (matchedPaper) {
							setSelectedPaperSize(matchedPaper.name);
						} else {
							setSelectedPaperSize("Custom");
						}

						// Show success message
						alert(`Design imported successfully! ${design.elements.length} elements loaded.`);
					}
				} catch (error) {
					console.error('Error importing design:', error);
					alert('Error importing design file. Please check if it\'s a valid bill design JSON file.');
				}
			};

			reader.readAsText(file);
		};

		fileInput.click();
	};

	const [previewData, setPreviewData] = useState(null);
	const [showLivePreview, setShowLivePreview] = useState(false);

	const getPreviewData = () => {
		return {
			invoiceNumber: "INV-001",
			customerName: "John Doe",
			tableNumber: "Table #5",
			date: "15/12/2025",
			time: "14:30",
			cashier: "Cashier Name",
			waiter: "Waiter Name",
			subtotal: "₹ 1500",
			cgst: "₹ 150",
			sgst: "₹ 150",
			grandTotal: "₹ 1800",
			companyInfo: {
				name: companyDetails?.name || logginedDB || "Restaurant",
				address: companyDetails?.add1 || "Address",
				phone: companyDetails?.mobile || "Phone",
				gst: companyDetails?.gst || "GST Number",
				logo: companyDetails?.img || ""
			}
		};
	};

	// Initialize preview data
	useEffect(() => {
		setPreviewData({
			invoiceNumber: "INV-001",
			customerName: "Sample Customer",
			tableNumber: "Table #1",
			date: new Date().toLocaleDateString(),
			time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
			cashier: "Cashier",
			waiter: "Waiter",
			subtotal: 1000,
			gst: { cgst: 50, sgst: 50, rate: 5 },
			grandTotal: 1100,
			companyInfo: {
				name: companyDetails?.name || logginedDB || "Restaurant",
				address: companyDetails?.add1 || "Address",
				phone: companyDetails?.mobile || "Phone",
				gst: companyDetails?.gst || "GST Number",
				logo: companyDetails?.img || ""
			},
			items: [],
			addons: [],
			allItems: []
		});
	}, [companyDetails, logginedDB]);

	// Function to handle paper size change
	const handlePaperSizeChange = (paperName) => {
		setSelectedPaperSize(paperName);

		if (paperName === "Custom") {
			// Keep current dimensions for custom
			return;
		}

		const paper = PAPER_SIZES.find(p => p.name === paperName);
		if (paper && paper.width && paper.height) {
			const newWidth = mmToPixels(paper.width);
			const newHeight = mmToPixels(paper.height);
			setCanvasSize({ width: newWidth, height: newHeight });

			// Adjust zoom if the canvas is too small to see clearly
			if (newWidth < 400 && zoomLevel < 150) {
				setZoomLevel(150);
			}
		}
	};

	// Zoom functions
	const zoomIn = () => {
		setZoomLevel(prev => Math.min(prev + 25, 400)); // Max 400% zoom
	};

	const zoomOut = () => {
		setZoomLevel(prev => Math.max(prev - 25, 25)); // Min 25% zoom
	};

	const resetZoom = () => {
		setZoomLevel(100);
	};

	const handleResetDesign = () => {
		if (window.confirm("Are you sure you want to reset? This will remove all elements but keep the canvas size.")) {
			setElements([]);
			setSelectedElement(null);
		}
	}

	const handleExportDesign = () => {
		const design = {
			elements,
			backgroundColor,
			canvasSize,
			exportedAt: new Date().toISOString(),
			version: "1.0"
		};

		const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(design, null, 2));
		const downloadAnchorNode = document.createElement('a');
		downloadAnchorNode.setAttribute("href", dataStr);
		downloadAnchorNode.setAttribute("download", `bill_design_${Date.now()}.json`);
		document.body.appendChild(downloadAnchorNode);
		downloadAnchorNode.click();
		downloadAnchorNode.remove();
	};

	const [showDataPanel, setShowDataPanel] = useState(true);

	const pixelsToInches = (px) => (px / 96).toFixed(2);
	const MIN_WIDTH_PIXELS = 20;

	const calculateNextYPosition = () => {
		if (elements.length === 0) return 50;

		const lastElement = elements[elements.length - 1];
		return lastElement.y + lastElement.height + 20;
	};

	const addCompanyInfoElement = (field, value, label, isImage = false, customY = null) => {
		if (!value) return;

		const yPos = customY !== null
			? customY
			: (elements.length > 0 ? elements[elements.length - 1].y + (elements[elements.length - 1].height || 44) + 10 : 50);

		let defaultWidth = field === "gst" ? 200 : 150;
		let defaultX = 50;

		if (field === "name") {
			defaultWidth = canvasSize.width;
			defaultX = 0;
		}

		const newElement = {
			id: `${field}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			type: isImage ? "image" : "text",
			content: value,
			x: defaultX,
			y: yPos,
			width: isImage ? 160 : defaultWidth,
			height: isImage ? 160 : 44,
			styles: getCompanyInfoStyles(field),
			companyField: field,
			originalLabel: label || field,
		};

		setElements((prev) => [...prev, newElement]);
		setSelectedElement(newElement.id);
	};

	const getCompanyInfoStyles = (field) => {
		const base = {
			fontSize: "8px",
			color: "#000000",
			fontWeight: "normal",
			textAlign: "left",
			fontFamily: "Arial, sans-serif",
			backgroundColor: "transparent",
		};

		switch (field) {
			case "name":
				return {
					...base,
					fontSize: "8px",
					fontWeight: "bold",
					textAlign: "center",
				};
			case "gst":
				return {
					...base,
					fontSize: "8px",
					fontWeight: "bold",
					fontStyle: "italic",
				};
			case "phone":
			case "email":
				return {
					...base,
					fontSize: "8px",
				};
			default:
				return base;
		}
	};

	const addDynamicField = (fieldType) => {
		const field = dynamicFields.find(f => f.type === fieldType);
		if (!field) return;

		const newElement = {
			id: `${fieldType}-${Date.now()}`,
			type: fieldType,
			content: field.placeholder,
			x: 50,
			y: calculateNextYPosition(),
			width: fieldType === 'items_table' ? canvasSize.width - 100 :
				fieldType === 'gst_summary' ? 200 :
					fieldType === 'date_time' ? 250 : 150,
			height: fieldType === 'items_table' ? 200 :
				fieldType === 'gst_summary' ? 80 : 44,
			styles: getDefaultStyles(fieldType),
		};

		setElements((prev) => [...prev, newElement]);
		setSelectedElement(newElement.id);
	};

	const addElement = (type) => {
		if (dynamicFields.some(field => field.type === type)) {
			addDynamicField(type);
			return;
		}

		const newElement = {
			id: Date.now().toString(),
			type,
			content: getDefaultContent(type),
			x: 50,
			y: calculateNextYPosition(),
			width: getDefaultWidth(type),
			height: getDefaultHeight(type),
			styles: getDefaultStyles(type),
		};
		setElements((prev) => [...prev, newElement]);
		setSelectedElement(newElement.id);
	};

	const getDefaultContent = (type) => {
		const dynamicField = dynamicFields.find(f => f.type === type);
		if (dynamicField) {
			return dynamicField.placeholder;
		}


		const map = {
			header: "SHERDINE",
			subheader: "INVOICE",
			text: "Sample Text",
			price: "₹ 0.00",
			line: "",
			image: "",
			invoice_number: "Invoice No: {{invoiceNumber}}",
			table_number: "Table: {{tableNumber}}",
			customer_name: "Name: {{customerName}}",
			date_time: "Date: {{date}} Time: {{time}}",
			cashier: "Cashier: {{cashier}}",
			waiter: "Waiter: {{waiter}}",
			subtotal: "Sub-Total: {{subtotal}}",
			cgst: "CGST: {{cgst}}",
			sgst: "SGST: {{sgst}}",
			total: "Total: {{grandTotal}}",
		};
		return map[type] || "New Element";
	};

	const getDefaultWidth = (type) => {
		if (type === 'items_table') return canvasSize.width - 100;
		if (type === 'gst_summary') return 200;
		if (type === 'date_time') return 250;
		if (type === 'image') return 160;
		if (type === 'line') return canvasSize.width - 100;

		const dynamicField = dynamicFields.find(f => f.type === type);
		if (dynamicField) {
			return dynamicField.type.includes('total') ? 150 : 180;
		}

		return 110;
	};

	const getDefaultHeight = (type) => {
		if (type === 'items_table') return 200;
		if (type === 'gst_summary') return 80;
		if (type === 'image') return 160;
		if (type === 'line') return 4;
		return 44;
	};

	const getDefaultStyles = (type) => {
		const base = {
			fontSize: "8px",
			color: "#000000",
			fontWeight: "normal",
			textAlign: "left",
			fontFamily: "Arial, sans-serif",
			backgroundColor: "transparent",
		};

		switch (type) {
			case 'invoice_number':
			case 'table_number':
			case 'customer_name':
			case 'date_time':
			case 'cashier':
			case 'waiter':
				return {
					...base,
					fontSize: "9px",
					fontWeight: "bold",
				};
			case 'subtotal':
			case 'cgst':
			case 'sgst':
			case 'total':
				return {
					...base,
					fontSize: "10px",
					fontWeight: "bold",
					textAlign: "right",
				};
			case 'items_table':
				return {
					...base,
					fontSize: "9px",
					border: "1px solid #ddd",
					padding: "5px",
				};
			case 'gst_summary':
				return {
					...base,
					fontSize: "10px",
					padding: "5px",
					borderTop: "1px dashed #999",
				};
			case "header":
				return {
					...base,
					fontSize: "8px",
					fontWeight: "bold",
					textAlign: "center",
				};
			case "subheader":
				return {
					...base,
					fontSize: "8px",
					fontWeight: "bold",
					textAlign: "center",
				};
			case "price":
				return {
					...base,
					fontWeight: "bold",
					textAlign: "right",
					fontSize: "8px",
				};
			case "line":
				return { borderTop: "2px dashed #999", backgroundColor: "transparent" };
			case "image":
				return { backgroundColor: "transparent" };
			default:
				return base;
		}
	};

	const moveElement = (id, x, y) => setElements(elements.map((el) => (el.id === id ? { ...el, x, y } : el)));
	const updateElement = (id, updates) => setElements(elements.map((el) => (el.id === id ? { ...el, ...updates } : el)));
	const resizeElement = (id, width, height) => {
		const el = elements.find((e) => e.id === id);
		if (!el) return;
		const minH = el.type === "line" ? 2 : 20;
		const w = Math.max(MIN_WIDTH_PIXELS, width);
		const h = Math.max(minH, height);
		setElements(elements.map((e) => (e.id === id ? { ...e, width: w, height: h } : e)));
	};

	const deleteElement = (id) => {
		setElements(elements.filter((el) => el.id !== id));
		setSelectedElement(null);
	};

	const handleSave = () => {
		const design = {
			elements,
			backgroundColor,
			canvasSize,
			savedAt: new Date().toISOString(),
		};
		if (typeof onSaveDesign === "function") onSaveDesign(design);
		else {
			localStorage.setItem("billDesign", JSON.stringify(design));
			alert("Design saved to localStorage!");
		}
	};

	const selectedEl = elements.find((el) => el.id === selectedElement);

	const updateCanvasWidth = (width) => {
		const minWidth = 2 * 96;
		const maxWidth = 15 * 96;
		const newWidth = Math.max(minWidth, Math.min(maxWidth, +width || minWidth));
		setCanvasSize({ ...canvasSize, width: newWidth });
		setSelectedPaperSize("Custom");
	};

	const updateCanvasHeight = (height) => {
		const minHeight = 2 * 96;
		const maxHeight = 15 * 96;
		const newHeight = Math.max(minHeight, Math.min(maxHeight, +height || minHeight));
		setCanvasSize({ ...canvasSize, height: newHeight });
		setSelectedPaperSize("Custom");
	};

	const elementTypes = [
		"header", "subheader", "text", "price", "line", "image",
		"invoice_number", "table_number", "customer_name", "date_time",
		"cashier", "waiter", "subtotal", "cgst", "sgst", "total",
		"items_table", "gst_summary"
	];

	return (
		<DndProvider backend={HTML5Backend}>
			<div className="flex flex-col h-screen font-sans bg-gray-100 overflow-hidden">
				<SherDineNav sideNav={(state) => handleSideNav(state)} falseState={falseState} />
				<div className="flex gap-3 flex-1 overflow-hidden">
					<div className="w-max h-full z-[9999]">
						<SideNav focus={"Print Design"} state={sideNav} falseState={(state) => setFalseState(state)} />
					</div>
					<div className="flex-1 flex flex-col overflow-hidden">
						{/* Mobile Header */}
						<div className="flex lg:hidden items-center justify-between p-3 bg-white border-b border-gray-200">
							<h3 className="font-semibold text-gray-800">Bill Designer</h3>
							<button
								onClick={() => setMobileToolbarOpen(!mobileToolbarOpen)}
								className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
							>
								{mobileToolbarOpen ? (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
									</svg>
								) : (
									<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
									</svg>
								)}
							</button>
						</div>

						{/* Desktop Toolbar */}
						<div className="hidden lg:flex bg-white border-b border-gray-200 shadow-sm">
							<div className="flex-1 px-3 pb-1">
								<div className="flex flex-nowrap items-start justify-between gap-1 h-full">
									{/* Left Section: Add Elements */}
									<div className="flex flex-col gap-2 lg:max-w-[200px] xl:max-w-[250px]">
										<h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Add Elements</h4>
										<div className="flex flex-wrap gap-1 items-center">
											{["header", "subheader", "text", "price", "line", "image"].map((t) => (
												<button
													key={t}
													onClick={() => addElement(t)}
													className="px-1.5  py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
												>
													{t.charAt(0).toUpperCase() + t.slice(1)}
												</button>
											))}
											<button
												onClick={() => addElement('invoice_number')}
												className="px-1.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
											>
												Dynamic
											</button>
										</div>
									</div>

									{/* Middle Section: Canvas Settings */}
									<div className="flex flex-row flex-nowrap items-start gap-4 pl-2 xl:gap-3 pb-2">
										{/* Paper Size */}
										<div className="flex flex-col gap-1 xl:px-3">
											<label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Paper Size</label>
											<select
												value={selectedPaperSize}
												onChange={(e) => handlePaperSizeChange(e.target.value)}
												className="w-40 2xl:w-48 xl:w-44 px-1.5 py-1.5 text-xs xl:text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
											>
												{PAPER_SIZES.map((paper) => (
													<option key={paper.name} value={paper.name}>
														{paper.name}
													</option>
												))}
											</select>
										</div>

										{/* Canvas Size with Background Color */}
										<div className="flex flex-col gap-1 xl:px-3">
											<label className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
												Canvas Size
												<div className="flex items-center gap-1">
													<input
														type="color"
														value={backgroundColor}
														onChange={(e) => setBackgroundColor(e.target.value)}
														className="w-5 h-5 rounded cursor-pointer border border-gray-300"
														title="Background color"
													/>
													<span className="text-xs text-gray-500">BG</span>
												</div>
											</label>
											<div className="flex flex-col gap-1 w-full">
												<div className="flex items-center gap-1">
													<div className="flex flex-col items-center">
														<div className="flex items-center gap-1">
															<span className="text-xs text-gray-500">W:</span>
															<input
																type="number"
																value={pixelsToMM(canvasSize.width)}
																onChange={(e) => {
																	const mm = parseFloat(e.target.value) || 58;
																	const clampedMM = Math.max(20, Math.min(400, mm));
																	updateCanvasWidth(mmToPixels(clampedMM));
																}}
																min="20"
																max="400"
																step="1"
																className="w-10 xl:w-11 2xl:w-12 px-2 py-1 xl:text-sm text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
															/>
															<span className="text-xs text-gray-500">mm</span>
														</div>
														<span className="text-[10px] text-gray-400">{canvasSize.width}px</span>
													</div>

													<div className="flex flex-col items-center">
														<div className="flex items-center gap-1">
															<span className="text-xs text-gray-500">H:</span>
															<input
																type="number"
																value={pixelsToMM(canvasSize.height)}
																onChange={(e) => {
																	const mm = parseFloat(e.target.value) || 297;
																	const clampedMM = Math.max(50, Math.min(1000, mm));
																	updateCanvasHeight(mmToPixels(clampedMM));
																}}
																min="50"
																max="1000"
																step="1"
																className="w-9 xl:w-11 2xl:w-12 px-2 py-1 xl:text-sm text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
															/>
															<span className="text-xs text-gray-500">mm</span>
														</div>
														<span className="text-[10px] text-gray-400">{canvasSize.height}px</span>
													</div>
												</div>
											</div>
										</div>

										{/* Zoom Controls - Vertical on small screens */}
										<div className="flex flex-col gap-1">
											<label className="text-xs font-bold  text-center text-gray-700 uppercase tracking-wide">Zoom</label>

											<div className="flex flex-col gap-1">
												<select
													value={zoomLevel}
													onChange={(e) => setZoomLevel(parseInt(e.target.value))}
													className="w-16 px-1 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
												>
													{[25, 50, 75, 100, 125, 150, 175, 200, 250, 300, 400].map((level) => (
														<option key={level} value={level}>
															{level}%
														</option>
													))}
												</select>
												<div className=" flex flex-row justify-between items-center">
													<button
														onClick={zoomOut}
														className="p-1.5 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
														title="Zoom Out"
													>
														<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
														</svg>
													</button>
													<button
														onClick={zoomIn}
														className="p-1.5 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
														title="Zoom In"
													>
														<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
														</svg>
													</button>

												</div>


											</div>
										</div>
									</div>

									{/* Right Section: Action Buttons */}
									<div className="flex justify-end h-full ml-5">
										<div className="flex flex-col gap-1">
											<label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Actions</label>
											<div className="flex flex-wrap flex-row gap-2 items-center w-full">
												<button
													onClick={() => setShowDataPanel(!showDataPanel)}
													className={`px-2.5 py-1.5 xl:px-3 xl:py-2 text-xs font-medium rounded transition-colors ${showDataPanel
														? "bg-blue-600 text-white"
														: "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
												>
													{showDataPanel ? "Hide Data" : "Data"}
												</button>
												<button
													onClick={() => setPreviewMode(!previewMode)}
													className={`px-2.5 py-1.5 xl:px-3 xl:py-2 text-xs font-medium rounded transition-colors ${previewMode
														? "bg-blue-600 text-white"
														: "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
												>
													{previewMode ? "Edit" : "Preview"}
												</button>
												<button
													onClick={() => setShowLivePreview(!showLivePreview)}
													className={`px-2.5 py-1.5 xl:px-3 xl:py-2 text-xs font-medium rounded transition-colors ${showLivePreview
														? "bg-purple-600 text-white"
														: "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
												>
													Live
												</button>
												<button
													onClick={handleSave}
													className="px-2.5 py-1.5 xl:px-3 xl:py-2 text-xs font-medium text-white bg-emerald-500 rounded hover:bg-emerald-600 transition-colors"
												>
													Save
												</button>
												<button
													onClick={() => window.confirm("Clear all elements?") && setElements([])}
													className="px-2.5 py-1.5 xl:px-3 xl:py-2 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
												>
													Clear
												</button>
												<CanvasControls
													showGrid={showGrid}
													setShowGrid={setShowGrid}
													showRulers={showRulers}
													setShowRulers={setShowRulers}
												/>
											</div>

										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Mobile Toolbar (Collapsible) */}
						{mobileToolbarOpen && (
							<div className="lg:hidden bg-white border-b border-gray-200 p-4">
								<div className="space-y-4">
									{/* Add Elements for Mobile */}
									<div>
										<h4 className="text-sm font-bold text-gray-700 mb-2">Add Elements</h4>
										<div className="grid grid-cols-3 gap-2">
											{["header", "subheader", "text", "price", "line", "image"].map((t) => (
												<button
													key={t}
													onClick={() => {
														addElement(t);
														setMobileToolbarOpen(false);
													}}
													className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
												>
													{t.charAt(0).toUpperCase() + t.slice(1)}
												</button>
											))}
											<button
												onClick={() => {
													addElement('invoice_number');
													setMobileToolbarOpen(false);
												}}
												className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors col-span-3"
											>
												Dynamic Fields
											</button>
										</div>
									</div>

									{/* Paper Size for Mobile */}
									<div>
										<h4 className="text-sm font-bold text-gray-700 mb-2">Paper Size</h4>
										<select
											value={selectedPaperSize}
											onChange={(e) => handlePaperSizeChange(e.target.value)}
											className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
										>
											{PAPER_SIZES.map((paper) => (
												<option key={paper.name} value={paper.name}>
													{paper.name}
												</option>
											))}
										</select>
									</div>

									{/* Canvas Size with Background Color for Mobile */}
									<div>
										<h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center justify-between">
											Canvas Size
											<div className="flex items-center gap-2">
												<span className="text-xs text-gray-500">BG Color:</span>
												<input
													type="color"
													value={backgroundColor}
													onChange={(e) => setBackgroundColor(e.target.value)}
													className="w-8 h-8 rounded cursor-pointer border border-gray-300"
													title="Background color"
												/>
											</div>
										</h4>
										<div className="grid grid-cols-2 gap-3">
											<div>
												<label className="block text-xs text-gray-600 mb-1">Width (mm)</label>
												<div className="flex items-center gap-2">
													<input
														type="number"
														value={pixelsToMM(canvasSize.width)}
														onChange={(e) => {
															const mm = parseFloat(e.target.value) || 58;
															const clampedMM = Math.max(20, Math.min(400, mm));
															updateCanvasWidth(mmToPixels(clampedMM));
														}}
														className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded"
													/>
													<span className="text-xs text-gray-500">mm</span>
												</div>
												<div className="text-xs text-gray-500 mt-1">{canvasSize.width}px</div>
											</div>
											<div>
												<label className="block text-xs text-gray-600 mb-1">Height (mm)</label>
												<div className="flex items-center gap-2">
													<input
														type="number"
														value={pixelsToMM(canvasSize.height)}
														onChange={(e) => {
															const mm = parseFloat(e.target.value) || 297;
															const clampedMM = Math.max(50, Math.min(1000, mm));
															updateCanvasHeight(mmToPixels(clampedMM));
														}}
														className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded"
													/>
													<span className="text-xs text-gray-500">mm</span>
												</div>
												<div className="text-xs text-gray-500 mt-1">{canvasSize.height}px</div>
											</div>
										</div>
									</div>

									{/* Zoom Controls for Mobile */}
									<div>
										<h4 className="text-sm font-bold text-gray-700 mb-2">Zoom</h4>
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<button
													onClick={zoomOut}
													className="p-3 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
													title="Zoom Out"
												>
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
													</svg>
												</button>

												<select
													value={zoomLevel}
													onChange={(e) => setZoomLevel(parseInt(e.target.value))}
													className="px-3 py-2 text-sm border border-gray-300 rounded"
												>
													{[25, 50, 75, 100, 125, 150, 175, 200, 250, 300, 400].map((level) => (
														<option key={level} value={level}>
															{level}%
														</option>
													))}
												</select>

												<button
													onClick={zoomIn}
													className="p-3 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
													title="Zoom In"
												>
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
													</svg>
												</button>
											</div>

											<button
												onClick={resetZoom}
												className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
												title="Reset Zoom"
											>
												100%
											</button>
										</div>
									</div>

									{/* Action Buttons for Mobile */}
									<div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
										<button
											onClick={() => setShowDataPanel(!showDataPanel)}
											className={`px-4 py-2 text-sm font-medium rounded transition-colors ${showDataPanel
												? "bg-blue-600 text-white"
												: "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
										>
											{showDataPanel ? "Hide Data" : "Show Data"}
										</button>
										<button
											onClick={() => setPreviewMode(!previewMode)}
											className={`px-4 py-2 text-sm font-medium rounded transition-colors ${previewMode
												? "bg-blue-600 text-white"
												: "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
										>
											{previewMode ? "Edit Mode" : "Preview"}
										</button>
										<button
											onClick={() => setShowLivePreview(!showLivePreview)}
											className={`px-4 py-2 text-sm font-medium rounded transition-colors col-span-2 ${showLivePreview
												? "bg-purple-600 text-white"
												: "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
										>
											{showLivePreview ? "Hide Live Preview" : "Live Preview"}
										</button>
										<button
											onClick={handleSave}
											className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 rounded hover:bg-emerald-600 transition-colors"
										>
											Save Design
										</button>
										<button
											onClick={() => window.confirm("Clear all elements?") && setElements([])}
											className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
										>
											Clear All
										</button>
									</div>
								</div>
							</div>
						)}

						<div className="flex gap-3 flex-1 overflow-hidden">
							{selectedEl && !previewMode && (
								<div className={`${mobileToolbarOpen ? "hidden lg:block" : "block"}`}>
									<EnhancedElementProperties
										element={selectedEl}
										onUpdate={updateElement}
										onResize={resizeElement}
										onDelete={deleteElement}
										minWidthPixels={MIN_WIDTH_PIXELS}
										pixelsToInches={pixelsToInches}
										getPreviewData={getPreviewData}
										collapsedSections={collapsedSections}  
										toggleSection={toggleSection}          
									/>
								</div>
							)}
							<div className="flex-1 flex justify-center items-start overflow-auto bg-gray-200 p-4">
								{showLivePreview && previewData ? (
									<div className="relative w-full flex justify-center">
										<BillDesignRenderer
											design={{ elements, backgroundColor, canvasSize }}
											dynamicData={previewData}
										/>
										<div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1 rounded text-sm">
											Live Preview
										</div>
									</div>
								) : (
									<div className="relative w-full flex justify-center items-start">
										{/* Inline-block wrapper to contain canvas and zoom indicator */}
										<div className="relative mt-10" style={{ width: `${canvasSize.width + 60}px` }}>
											{/* Zoom Indicator - Positioned above canvas wrapper */}
											<div
												className="absolute z-50 pointer-events-none"
												style={{
													top: "-35px",
													left: "50%",
													transform: "translateX(-50%)",
												}}
											>
												<div className="bg-black/80 text-white px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm shadow-sm border border-white/10 whitespace-nowrap">
													Zoom: {zoomLevel}%
												</div>
											</div>

											{/* Scaled canvas container */}
											<div
												className="transition-transform duration-200 ease-in-out mt-3"
												style={{
													transform: `scale(${zoomLevel / 100})`,
													transformOrigin: 'top center'
												}}
											>
												<EnhancedDesignCanvas
													elements={elements}
													selectedElement={selectedElement}
													onSelect={setSelectedElement}
													onMove={moveElement}
													onUpdate={updateElement}
													onResize={resizeElement}
													previewMode={previewMode}
													backgroundColor={backgroundColor}
													canvasSize={canvasSize}
													minWidthPixels={MIN_WIDTH_PIXELS}
													pixelsToInches={pixelsToInches}
													zoomLevel={zoomLevel}
													showGrid={showGrid}       
													showRulers={showRulers}    
												/>
											</div>
										</div>
									</div>
								)}
							</div>
							{showDataPanel && (
								<BackendDataPanel
									onAddCompanyInfo={addCompanyInfoElement}
									onAddDynamicField={addDynamicField}
									isMobileToolbarOpen={mobileToolbarOpen}
									onReset={handleResetDesign}
									onExport={handleExportDesign}
									onImport={handleImportDesign}  
									collapsedSections={collapsedSections}  
									toggleSection={toggleSection}          
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		</DndProvider>
	);
};

const BackendDataPanel = ({ onAddCompanyInfo, isMobileToolbarOpen,
	onAddDynamicField, onReset, onExport, collapsedSections, 
	toggleSection, onImport }) => {
	const { logginedDB, companyDetails } = useContext(DBContext);
	const companyInfos = {
		companyInfo: {
			name: companyDetails?.name || logginedDB || "",
			address1: companyDetails?.add1 || "",
			address2: companyDetails?.add2 || "",
			address3: companyDetails?.add3 || "",
			phone: companyDetails?.mobile || "",
			email: companyDetails?.email || "",
			gst: companyDetails?.gst || "",
			image: companyDetails?.img || "",
		},
	};

	const dragDataRef = useRef(null);
	const handleDragStart = (e, field, value, label, isImage = false) => {
		if (!value) {
			e.preventDefault();
			return;
		}

		dragDataRef.current = {
			type: isImage ? "companyImage" : "companyInfo",
			field,
			value,
			label,
			isImage,
			timestamp: Date.now(),
		};

		const dragId = `company-${field}-${Date.now()}`;
		e.dataTransfer.setData("text/plain", dragId);
		e.dataTransfer.setData(
			"application/json",
			JSON.stringify({
				type: isImage ? "companyImage" : "companyInfo",
				field,
				value,
				label,
				isImage,
				id: dragId,
			})
		);
		e.dataTransfer.effectAllowed = "copy";

		e.currentTarget.classList.add("opacity-50");
	};

	const handleDragEnd = (e) => {
		e.currentTarget.classList.remove("opacity-50");
		dragDataRef.current = null;
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "copy";
	};

	const handleDropOnCanvas = (e) => {
		e.preventDefault();

		try {
			let data;
			try {
				const jsonData = e.dataTransfer.getData("application/json");
				if (jsonData) {
					data = JSON.parse(jsonData);
				} else {
					data = dragDataRef.current;
				}
			} catch (error) {
				data = dragDataRef.current;
			}

			if (data) {
				if (data.type === "companyImage") {
					onAddCompanyInfo(data.field, data.value, data.label, true);
				} else if (data.type === "companyInfo") {
					onAddCompanyInfo(data.field, data.value, data.label);
				} else if (data.type === "dynamic_field") {
					onAddDynamicField(data.fieldType);
				}
			}
		} catch (error) {
			console.error("Error handling drop:", error);
		}
	};

	useEffect(() => {
		const canvasContainer = document.querySelector(".canvas-container");
		if (canvasContainer) {
			canvasContainer.addEventListener("dragover", handleDragOver);
			canvasContainer.addEventListener("drop", handleDropOnCanvas);

			return () => {
				canvasContainer.removeEventListener("dragover", handleDragOver);
				canvasContainer.removeEventListener("drop", handleDropOnCanvas);
			};
		}
	}, [onAddCompanyInfo, onAddDynamicField])

	useEffect(() => {
		const draggableItems = document.querySelectorAll(".draggable-item");
		const handleItemDragEnd = (e) => {
			e.currentTarget.classList.remove("opacity-50");
		};

		draggableItems.forEach((item) => {
			item.addEventListener("dragend", handleItemDragEnd);
		});

		return () => {
			draggableItems.forEach((item) => {
				item.removeEventListener("dragend", handleItemDragEnd);
			});
		};
	}, [companyInfos]);
	return (
		<div className={`${isMobileToolbarOpen ? "hidden lg:block" : "block"} w-80 bg-white border-l border-gray-200 p-4 overflow-y-auto shadow-lg h-full flex flex-col`}>
			<div className="flex justify-between items-center mb-4">
				<h3 className="text-lg font-semibold text-gray-800">Data Panel</h3>
				<button
					onClick={() => window.location.reload()}
					className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
					title="Refresh data"
				>
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
					</svg>
				</button>
			</div>

			<div className="flex-1 overflow-y-auto space-y-6">
				<div className="border border-gray-200 rounded">
					<div className="flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer" onClick={() => toggleSection('companyData')}>
						<h4 className="text-sm font-bold text-gray-700">Company Data</h4>
						<svg
							className={`w-4 h-4 transform transition-transform ${collapsedSections.companyData ? '' : 'rotate-180'}`}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
						</svg>
					</div>
					{!collapsedSections.companyData && (
						<div className="space-y-3 p-3">
							{companyInfos.companyInfo.image && (
								<div
									draggable="true"
									onDragStart={(e) => handleDragStart(e, "image", companyInfos.companyInfo.image, "Company Logo", true)}
									onDragEnd={handleDragEnd}
									className="p-3 bg-blue-50 border border-blue-200 rounded cursor-move hover:bg-blue-100 transition-colors draggable-item"
								>
									<div className="flex items-center gap-2 mb-2">
										<svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 002 2z" />
										</svg>
										<span className="text-xs text-gray-500">Company Logo</span>
									</div>
									<div className="w-full h-40 overflow-hidden rounded border border-gray-300 bg-white">
										<img
											src={companyInfos.companyInfo.image}
											alt="Company Logo"
											className="w-full h-full object-contain p-2"
											onError={(e) => {
												e.target.src = "https://via.placeholder.com/150x150?text=Logo";
											}}
										/>
									</div>
									<div className="text-xs text-blue-600 mt-2 text-center">Drag to canvas</div>
								</div>
							)}

							{companyInfos.companyInfo.name && (
								<div
									draggable="true"
									onDragStart={(e) => handleDragStart(e, "name", companyInfos.companyInfo.name, "Company Name")}
									onDragEnd={handleDragEnd}
									className="p-3 bg-blue-50 border border-blue-200 rounded cursor-move hover:bg-blue-100 transition-colors draggable-item"
								>
									<div className="flex items-center gap-2">
										<svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<span className="text-xs text-gray-500">Company Name</span>
									</div>
									<span className="text-sm font-medium block mt-1">{companyInfos.companyInfo.name}</span>
									<div className="text-xs text-blue-600 mt-1">Drag to canvas</div>
								</div>
							)}

							{companyInfos.companyInfo.gst && (
								<div
									draggable="true"
									onDragStart={(e) => handleDragStart(e, "gst", companyInfos.companyInfo.gst, "GST Number")}
									onDragEnd={handleDragEnd}
									className="p-3 bg-yellow-50 border border-yellow-200 rounded cursor-move hover:bg-yellow-100 transition-colors draggable-item"
								>
									<div className="flex items-center gap-2">
										<svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
										</svg>
										<span className="text-xs text-gray-500">GST Number</span>
									</div>
									<span className="text-sm font-mono block mt-1">{companyInfos.companyInfo.gst}</span>
									<div className="text-xs text-yellow-600 mt-1">Drag to canvas</div>
								</div>
							)}

							<button
								onClick={() => {
									let currentY = 50;
									Object.entries(companyInfos.companyInfo).forEach(([field, value]) => {
										if (value && field !== "image") {
											const label = field.charAt(0).toUpperCase() + field.slice(1);
											onAddCompanyInfo(field, value, label, false, currentY);
											currentY += 60;
										}
									});
								}}
								className="w-full p-3 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 transition-colors text-emerald-700 font-medium"
							>
								Add All Company Info (Text)
							</button>
						</div>
					)}
				</div>

				<div className="border border-gray-200 rounded">
					<div
						className="flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer"
						onClick={() => toggleSection('dynamicFields')}
					>
						<h4 className="text-sm font-bold text-gray-700">Dynamic Bill Fields</h4>
						<svg
							className={`w-4 h-4 transform transition-transform ${collapsedSections.dynamicFields ? '' : 'rotate-180'}`}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
						</svg>
					</div>

					{!collapsedSections.dynamicFields && (
						<div className="p-3">
							<p className="text-xs text-gray-500 mb-3">
								These fields will be replaced with actual data when printing
							</p>

							<div className="grid grid-cols-1 gap-2">
								{dynamicFields.map((field) => (
									<button
										key={field.type}
										onClick={() => onAddDynamicField(field.type)}
										className="w-full text-left p-3 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 transition-colors"
										draggable="true"
										onDragStart={(e) => {
											e.dataTransfer.setData('application/json', JSON.stringify({
												type: 'dynamic_field',
												fieldType: field.type,
												content: field.placeholder,
												label: field.label
											}));
										}}
										onDragEnd={handleDragEnd}
									>
										<div className="flex justify-between items-center">
											<span className="font-medium text-purple-700">{field.label}</span>
											<span className="text-xs text-purple-500 bg-purple-100 px-2 py-1 rounded">
												Dynamic
											</span>
										</div>
										{field.placeholder && (
											<div className="text-xs text-purple-600 mt-1 font-mono truncate">
												{field.placeholder}
											</div>
										)}
									</button>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Quick Actions Section - Collapsible */}
				<div className="border border-gray-200 rounded">
					<div
						className="flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer"
						onClick={() => toggleSection('quickActions')}
					>
						<h4 className="text-sm font-bold text-gray-700">Quick Actions</h4>
						<svg
							className={`w-4 h-4 transform transition-transform ${collapsedSections.quickActions ? '' : 'rotate-180'}`}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
						</svg>
					</div>

					{!collapsedSections.quickActions && (
						<div className="p-3 space-y-2">
							<button
								onClick={onExport}
								className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm"
							>
								Export Current Design
							</button>
							<button className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm">Save as Template</button>
							<button
								onClick={onImport}
								className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm"
							>
								Import Design 
							</button>
							<button
								onClick={onReset}
								className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm"
							>
								Reset to Default
							</button>
						</div>
					)}
				</div>
			</div>

			<div className="pt-4 mt-4 border-t border-gray-200">
				<p className="text-xs text-gray-500 text-center">Drag items to the canvas</p>
			</div>
		</div>
	);
};

// Add CanvasControls component for Grid/Rulers toggle
const CanvasControls = ({ showGrid, setShowGrid, showRulers, setShowRulers }) => {
	return (
		<>
			<button
				onClick={() => setShowGrid(!showGrid)}
				className={`px-2.5 py-1.5 xl:px-3 xl:py-2 text-xs font-medium rounded transition-colors flex items-center gap-2 ${showGrid ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
			>
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
				</svg>
				Grid {showGrid ? "On" : "Off"}
			</button>
			<button
				onClick={() => setShowRulers(!showRulers)}
				className={`px-2.5 py-1.5 xl:px-3 xl:py-2 text-xs font-medium rounded transition-colors flex items-center gap-2 ${showRulers ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
			>
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
				</svg>
				Rulers {showRulers ? "On" : "Off"}
			</button>
		</>
	);
};
// Update EnhancedDesignCanvas to accept showGrid and showRulers props
const EnhancedDesignCanvas = ({
	elements,
	selectedElement,
	onSelect,
	onMove,
	onUpdate,
	onResize,
	previewMode,
	backgroundColor = "#ffffff",
	canvasSize = { width: 850, height: 1100 },
	minWidthPixels = 50,
	pixelsToInches,
	zoomLevel = 100,
	showGrid,
	showRulers,
}) => {
	const canvasRef = useRef(null);
	const containerRef = useRef(null);

	const INCH_PX = 96;
	const rulerSize = 30;

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.classList.add("canvas-container");
		}
	}, []);

	return (
		<div
			ref={containerRef}
			className="relative w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 select-none canvas-container"
		>
			{/* Canvas controls moved to top-right corner in parent component */}

			{/* Rest of the canvas component remains the same... */}
			<div
				className="absolute top-0 left-0 z-40 transition-opacity duration-300"
				style={{
					width: rulerSize,
					height: rulerSize,
					opacity: showRulers ? 1 : 0,
					pointerEvents: showRulers ? "auto" : "none",
				}}
			>
				{showRulers && (
					<div
						className="absolute top-0 left-0 bg-gray-100 border-r border-b border-gray-500"
						style={{ width: rulerSize, height: rulerSize }}
					/>
				)}
			</div>

			<div
				className="absolute z-40 transition-opacity duration-300"
				style={{
					top: 0,
					left: rulerSize,
					height: rulerSize,
					width: canvasSize.width,
					opacity: showRulers ? 1 : 0,
					pointerEvents: showRulers ? "auto" : "none",
				}}
			>
				{showRulers && (
					<div className="relative h-full bg-gray-100 border-b border-gray-400">
						{Array.from({ length: Math.ceil(canvasSize.width / INCH_PX) + 1 }, (_, i) => (
							<div key={`top-${i}`} className="absolute" style={{ left: `${i * INCH_PX}px` }}>
								<div className="absolute top-0 w-px bg-gray-800" style={{ height: 14 }} />

								<span
									className="absolute text-[10px] font-bold text-gray-800"
									style={{
										top: 16,
										left: 1,
										transform: "translateX(-50%)",
									}}
								>
									{i}
								</span>
								<div className="absolute top-0 w-px bg-gray-600" style={{ height: 9, left: INCH_PX / 2 }} />
								{[0.25, 0.75].map((q) => (
									<div
										key={q}
										className="absolute top-0 w-px bg-gray-500"
										style={{ height: 6, left: q * INCH_PX }}
									/>
								))}
							</div>
						))}
					</div>
				)}
			</div>

			<div
				className="absolute z-40 transition-opacity duration-300"
				style={{
					left: 0,
					top: rulerSize,
					width: rulerSize,
					height: canvasSize.height,
					opacity: showRulers ? 1 : 0,
					pointerEvents: showRulers ? "auto" : "none",
				}}
			>
				{showRulers && (
					<div className="relative w-full h-full bg-gray-100 border-r border-gray-400">
						{Array.from({ length: Math.ceil(canvasSize.height / INCH_PX) + 1 }, (_, i) => (
							<div key={`left-${i}`} className="absolute" style={{ top: `${i * INCH_PX}px` }}>
								<div className="absolute left-0 h-px bg-gray-800" style={{ width: 14 }} />

								<span
									className="absolute text-[10px] font-bold text-gray-800 origin-left whitespace-nowrap"
									style={{
										left: 18,
										top: 3,
										transform: "rotate(-90deg)",
										transformOrigin: "0 0",
									}}
								>
									{i}
								</span>
								<div className="absolute left-0 h-px bg-gray-600" style={{ width: 9, top: INCH_PX / 2 }} />
								{[0.25, 0.75].map((q) => (
									<div
										key={q}
										className="absolute left-0 h-px bg-gray-500"
										style={{ width: 6, top: q * INCH_PX }}
									/>
								))}
							</div>
						))}
					</div>
				)}
			</div>

			<div
				ref={canvasRef}
				className="absolute bg-white shadow-2xl overflow-hidden transition-shadow duration-300"
				style={{
					top: rulerSize,
					left: rulerSize,
					width: canvasSize.width,
					height: canvasSize.height,
					boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
					backgroundColor,
				}}
			>
				{showGrid && (
					<div
						className="absolute inset-0 pointer-events-none z-10"
						style={{
							backgroundImage: `
                            /* 1/16 inch – finest lines (every 6px) */
                            linear-gradient(to right, #cbd5e1 1px, transparent 1px),
                            linear-gradient(to bottom, #cbd5e1 .5px, transparent 1px),

                            /* 1/8 inch – slightly bolder (every 12px) */
                            linear-gradient(to right, #94a3b8 1px, transparent 1px),
                            linear-gradient(to bottom, #94a3b8 1px, transparent 1px),

                            /* 1/2 inch – medium lines (every 48px) */
                            linear-gradient(to right, #64748b 1px, transparent 1px),
                            linear-gradient(to bottom, #64748b 1px, transparent 1px),

                            /* 1 inch – strongest lines (every 96px) */
                            linear-gradient(to right, #475569 1.5px, transparent 1.5px),
                            linear-gradient(to bottom, #475569 1.5px, transparent 1.5px)
                        `,
							backgroundSize: `
                            6px 6px,       /* 1/16 inch */
                            6px 6px,       /* vertical fine lines */
                            12px 12px,     /* 1/8 inch */
                            12px 12px,
                            48px 48px,     /* 1/2 inch */
                            48px 48px,
                            96px 96px,     /* 1 inch */
                            96px 96px
                        `,
							backgroundPosition: "0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0",
							opacity: 0.4,
						}}
					/>
				)}

				{elements.map((el) => (
					<EnhancedDraggableElement
						key={el.id}
						element={el}
						isSelected={selectedElement === el.id}
						onSelect={onSelect}
						onMove={onMove}
						onUpdate={onUpdate}
						onResize={onResize}
						previewMode={previewMode}
						canvasRef={canvasRef}
						canvasSize={canvasSize}
						minWidthPixels={minWidthPixels}
						zoomLevel={zoomLevel}
					/>
				))}
			</div>



			<div
				className="absolute z-50"
				style={{
					top: `calc(${rulerSize}px + ${canvasSize.height}px + 10px)`,
					left: `calc(${rulerSize}px + ${canvasSize.width / 2}px)`,
					transform: "translateX(-50%)",
				}}
			>
				<div className="bg-black/80 text-white px-4 py-2 rounded-full text-xs font-medium backdrop-blur-sm whitespace-nowrap">
					{canvasSize.width} × {canvasSize.height}px → {pixelsToInches(canvasSize.width)}" ×{" "}
					{pixelsToInches(canvasSize.height)}" @ 96 DPI
				</div>
			</div>
		</div>
	);
};

const EnhancedDraggableElement = ({
	element,
	isSelected,
	onSelect,
	onMove,
	onResize,
	onUpdate,
	previewMode,
	canvasRef,
	canvasSize,
	zoomLevel = 100, 
}) => {
	const [isDragging, setIsDragging] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeDirection, setResizeDirection] = useState(null);
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

	const px = {
		x: element.x,
		y: element.y,
		width: element.width,
		height: element.height,
	};

	const baseFontSize = parseInt(element.styles?.fontSize || "8");
	const scaledFontSize = Math.round(baseFontSize * (canvasSize.width / 192));

	const style = {
		position: "absolute",
		left: `${px.x}px`,
		top: `${px.y}px`,
		width: `${px.width}px`,
		height: `${px.height}px`,
		border: isSelected && !previewMode ? "2px dashed #3b82f6" : "none",
		cursor: previewMode ? "default" : "move",
		opacity: isDragging || isResizing ? 0.7 : 1,
		padding: element.type === "image" ? "0" : "4px 8px",
		boxSizing: "border-box",
		overflow: "hidden",
		fontSize: element.type !== "image" ? `${scaledFontSize}px` : undefined,
		lineHeight: element.type !== "image" ? "1.4" : undefined,
		userSelect: "none",
		...element.styles,
	};

	const handleMouseDown = (e) => {
		if (previewMode) return;
		e.stopPropagation();
		onSelect(element.id);
		const rect = e.currentTarget.getBoundingClientRect();
		const scale = zoomLevel / 100;
		setDragOffset({ 
			x: e.clientX - rect.left / scale, 
			y: e.clientY - rect.top / scale 
		});
		setIsDragging(true);
	};

	useEffect(() => {
		const mouseMove = (e) => {
			if (isResizing && resizeDirection) {
				// Handle resizing with zoom
				const canvasRect = canvasRef.current.getBoundingClientRect();
				const scale = zoomLevel / 100;
				
				// Adjust mouse coordinates for zoom
				const mouseX = (e.clientX - canvasRect.left) / scale;
				const mouseY = (e.clientY - canvasRect.top) / scale;

				let newX = element.x;
				let newY = element.y;
				let newWidth = element.width;
				let newHeight = element.height;

				const minSize = element.type === "line" ? 2 : 20;

				// Handle different resize directions
				if (resizeDirection.includes("top")) {
					const deltaY = mouseY - element.y;
					newHeight = Math.max(minSize, element.height - deltaY);
					newY = element.y + (element.height - newHeight);
				}
				if (resizeDirection.includes("bottom")) {
					const deltaY = mouseY - (element.y + element.height);
					newHeight = Math.max(minSize, element.height + deltaY);
				}
				if (resizeDirection.includes("left")) {
					const deltaX = mouseX - element.x;
					newWidth = Math.max(minSize, element.width - deltaX);
					newX = element.x + (element.width - newWidth);
				}
				if (resizeDirection.includes("right")) {
					const deltaX = mouseX - (element.x + element.width);
					newWidth = Math.max(minSize, element.width + deltaX);
				}

				onResize(element.id, newWidth, newHeight);
				if (newX !== element.x || newY !== element.y) {
					onMove(element.id, newX, newY);
				}
			} else if (isDragging && !previewMode && canvasRef?.current) {
				const canvasRect = canvasRef.current.getBoundingClientRect();
				const scale = zoomLevel / 100;
				
				// Adjust mouse coordinates for zoom
				let newX = (e.clientX - canvasRect.left) / scale - dragOffset.x;
				let newY = (e.clientY - canvasRect.top) / scale - dragOffset.y;

				const padding = 5;
				const canvasWidth = canvasSize.width;
				const canvasHeight = canvasSize.height;
				
				newX = Math.max(padding, Math.min(newX, canvasWidth - px.width - padding));
				newY = Math.max(padding, Math.min(newY, canvasHeight - px.height - padding));

				onMove(element.id, newX, newY);
			}
		};

		const mouseUp = () => {
			setIsDragging(false);
			setIsResizing(false);
			setResizeDirection(null);
		};

		if (isDragging || isResizing) {
			document.addEventListener("mousemove", mouseMove);
			document.addEventListener("mouseup", mouseUp);
		}

		return () => {
			document.removeEventListener("mousemove", mouseMove);
			document.removeEventListener("mouseup", mouseUp);
		};
	}, [isDragging, isResizing, resizeDirection,
		dragOffset, previewMode, canvasSize, px.width, px.height,
		onMove, onResize, element, canvasRef, zoomLevel]);

	const handleResizeStart = (e, direction) => {
		if (previewMode) return;
		e.stopPropagation();
		e.preventDefault();
		setIsResizing(true);
		setResizeDirection(direction);
		onSelect(element.id);
	};

	// For text alignment in preview mode
	const getTextAlignClass = () => {
		const align = element.styles?.textAlign || "left";
		switch (align) {
			case "left": return "text-left";
			case "center": return "text-center";
			case "right": return "text-right";
			default: return "text-left";
		}
	};

	const getFontStyle = () => {
		const fontStyle = element.styles?.fontStyle || "normal";
		return fontStyle === "italic" ? "italic" : "normal";
	};

	const getTextTransform = () => {
		const textTransform = element.styles?.textTransform || "none";
		return textTransform;
	};

	const resizeHandleStyle = "absolute w-3 h-3 bg-blue-600 rounded-full border-2 border-white cursor-pointer z-10";

	const shouldShowTextarea = () => {
		if (previewMode) return false;

		const editableTypes = [
			"text", "header", "subheader", "price",
			"invoice_number", "table_number", "customer_name",
			"date_time", "cashier", "waiter", "subtotal",
			"cgst", "sgst", "total"
		];

		return editableTypes.includes(element.type);
	};

	// Check if element is a special dynamic element (items_table or gst_summary)
	const isSpecialElement = () => {
		return element.type === "items_table" || element.type === "gst_summary";
	};

	return (
		<div
			style={style}
			onMouseDown={handleMouseDown}
			className="select-none relative group"
			draggable={false}
		>
			{element.type === "line" ? null : element.type === "image" ? (
				element.content ? (
					<img
						src={element.content}
						alt=""
						className="w-full h-full object-contain"
						draggable={false}
						style={{
							objectFit: element.styles?.objectFit || "contain",
							borderRadius: element.styles?.borderRadius || "0px",
							opacity: element.styles?.opacity || 1,
							borderColor: element.styles?.borderColor,
							borderWidth: element.styles?.borderWidth || "0px",
							borderStyle: element.styles?.borderStyle || "solid",
						}}
					/>
				) : (
					<label className="flex items-center justify-center w-full h-full border-2 border-dashed border-gray-400 rounded text-xs bg-gray-50 cursor-pointer hover:bg-gray-100 transition">
						Upload Image
						<input
							type="file"
							accept="image/*"
							onChange={(e) => {
								const file = e.target.files[0];
								if (file) {
									const reader = new FileReader();
									reader.onload = (ev) => onUpdate(element.id, { content: ev.target.result });
									reader.readAsDataURL(file);
								}
							}}
							className="hidden"
						/>
					</label>
				)
			) : isSpecialElement() ? (
				// Special elements show preview content
				<div
					className={`w-full h-full flex items-center justify-center whitespace-pre-wrap break-words ${getTextAlignClass()}`}
					style={{
						fontSize: scaledFontSize,
						fontStyle: getFontStyle(),
						textTransform: getTextTransform(),
						fontWeight: element.styles?.fontWeight || "normal",
						color: element.styles?.color || "#000000",
						fontFamily: element.styles?.fontFamily || "Arial, sans-serif",
						letterSpacing: element.styles?.letterSpacing || "0px",
						lineHeight: element.styles?.lineHeight || "1.4",
					}}
				>
					{element.type === "items_table" ? "Items Table (Dynamic)" : "GST Summary (Dynamic)"}
				</div>
			) : shouldShowTextarea() ? (
				// Editable text elements (including dynamic fields)
				<textarea
					value={element.content}
					onChange={(e) => onUpdate(element.id, { content: e.target.value })}
					className="w-full h-full bg-transparent border-none outline-none resize-none"
					style={{
						fontSize: scaledFontSize,
						fontStyle: element.styles?.fontStyle || "normal",
						textTransform: element.styles?.textTransform || "none",
						fontWeight: element.styles?.fontWeight || "normal",
						color: element.styles?.color || "#000000",
						fontFamily: element.styles?.fontFamily || "Arial, sans-serif",
						textAlign: element.styles?.textAlign || "left",
						letterSpacing: element.styles?.letterSpacing || "0px",
						lineHeight: element.styles?.lineHeight || "1.4",
					}}
					spellCheck={false}
				/>
			) : (
				// Preview mode for text elements
				<div
					className={`w-full h-full flex items-center justify-center whitespace-pre-wrap break-words ${getTextAlignClass()}`}
					style={{
						fontSize: scaledFontSize,
						fontStyle: getFontStyle(),
						textTransform: getTextTransform(),
						fontWeight: element.styles?.fontWeight || "normal",
						color: element.styles?.color || "#000000",
						fontFamily: element.styles?.fontFamily || "Arial, sans-serif",
						letterSpacing: element.styles?.letterSpacing || "0px",
						lineHeight: element.styles?.lineHeight || "1.4",
					}}
				>
					{element.content}
				</div>
			)}

			{/* Resize Handles */}
			{isSelected && !previewMode && element.type !== "line" && (
				<>
					<div
						className={`${resizeHandleStyle} -top-1.5 -left-1.5 cursor-nw-resize`}
						onMouseDown={(e) => handleResizeStart(e, "top-left")}
					/>

					<div
						className={`${resizeHandleStyle} top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 cursor-n-resize`}
						onMouseDown={(e) => handleResizeStart(e, "top")}
					/>

					<div
						className={`${resizeHandleStyle} -top-1.5 -right-1.5 cursor-ne-resize`}
						onMouseDown={(e) => handleResizeStart(e, "top-right")}
					/>

					<div
						className={`${resizeHandleStyle} top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 cursor-w-resize`}
						onMouseDown={(e) => handleResizeStart(e, "left")}
					/>

					<div
						className={`${resizeHandleStyle} top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 cursor-e-resize`}
						onMouseDown={(e) => handleResizeStart(e, "right")}
					/>

					<div
						className={`${resizeHandleStyle} -bottom-1.5 -left-1.5 cursor-sw-resize`}
						onMouseDown={(e) => handleResizeStart(e, "bottom-left")}
					/>

					<div
						className={`${resizeHandleStyle} bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 cursor-s-resize`}
						onMouseDown={(e) => handleResizeStart(e, "bottom")}
					/>

					<div
						className={`${resizeHandleStyle} -bottom-1.5 -right-1.5 cursor-se-resize`}
						onMouseDown={(e) => handleResizeStart(e, "bottom-right")}
					/>

					<div
						className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-blue-600 rounded-full border-2 border-white cursor-grab flex items-center justify-center"
						onMouseDown={(e) => {
							e.stopPropagation();
						}}
					>
						<svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
							<path fillRule="evenodd" d="M10 3a1 1 0 00-1 1v5.586L5.707 7.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L11 9.586V4a1 1 0 00-1-1z" clipRule="evenodd" />
						</svg>
					</div>
				</>
			)}

			{isSelected && !previewMode && (
				<div className="absolute -top-7 left-0 bg-blue-600 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
					{element.width} × {element.height} px
				</div>
			)}
		</div>
	);
};


const EnhancedElementProperties = ({ element, onUpdate,
	onResize, onDelete, minWidthPixels, pixelsToInches,
	getPreviewData, collapsedSections, toggleSection }) => {
	const [activeTab, setActiveTab] = useState("layout");
	const [showPreview, setShowPreview] = useState(false);
	const [isTypeHovered, setIsTypeHovered] = useState(false);

	const getPreviewText = (text) => {
		const previewData = getPreviewData();
		let parsed = text;

		// Replace placeholders with preview data
		const replacements = {
			'{{invoiceNumber}}': previewData.invoiceNumber,
			'{{customerName}}': previewData.customerName,
			'{{tableNumber}}': previewData.tableNumber,
			'{{date}}': previewData.date,
			'{{time}}': previewData.time,
			'{{cashier}}': previewData.cashier,
			'{{waiter}}': previewData.waiter,
			'{{subtotal}}': previewData.subtotal,
			'{{cgst}}': previewData.cgst,
			'{{sgst}}': previewData.sgst,
			'{{grandTotal}}': previewData.grandTotal,
			'{{companyName}}': previewData.companyInfo.name,
			'{{companyAddress}}': previewData.companyInfo.address,
			'{{companyPhone}}': previewData.companyInfo.phone,
			'{{companyGST}}': previewData.companyInfo.gst,
		};

		Object.entries(replacements).forEach(([placeholder, value]) => {
			parsed = parsed.replace(new RegExp(placeholder, 'g'), value);
		});

		return parsed;
	};

	const handleStyle = (prop, val) => {
		const updatedStyles = {
			...element.styles,
			[prop]: val
		};
		onUpdate(element.id, { styles: updatedStyles });
	};

	const getCurrentLineColor = () => {
		const match = element.styles?.borderTop?.match(/#(......|...)|\w+$/);
		return match ? match[0] : "#000000";
	};

	const handleLineStyleChange = (style) => {
		const currentColor = getCurrentLineColor();
		handleStyle("borderTop", `2px ${style} ${currentColor}`);
	};

	const handleLineColorChange = (color) => {
		const currentStyle = element.styles?.borderTop || "2px solid #000000";
		const match = currentStyle.match(/(solid|dashed|dotted)/);
		const styleType = match ? match[0] : "solid";
		handleStyle("borderTop", `2px ${styleType} ${color}`);
	};

	const handleImage = (e) => {
		const file = e.target.files[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = (ev) => onUpdate(element.id, {
				content: ev.target.result
			});
			reader.readAsDataURL(file);
		}
	};

	const getTextAlignValue = () => {
		return element.styles?.textAlign || "left";
	};

	const getFontStyleValue = () => {
		return element.styles?.fontStyle || "normal";
	};

	const getTextTransformValue = () => {
		return element.styles?.textTransform || "none";
	};

	const getTabs = () => {
		const baseTabs = ["layout"];
		const isTextBasedElement = [
			"text", "header", "subheader", "price",
			"invoice_number", "table_number", "customer_name",
			"date_time", "cashier", "waiter", "subtotal",
			"cgst", "sgst", "total"
		].includes(element.type);

		if (isTextBasedElement) {
			baseTabs.push("text", "style");
		} else if (element.type === "image") {
			baseTabs.push("image");
		} else if (element.type === "line") {
			baseTabs.push("line");
		} else if (element.type === "items_table" || element.type === "gst_summary") {
			// Special dynamic elements only get layout tab
			console.log("Dynamic element detected:", element.type);
		}

		return baseTabs;
	};

	const tabs = getTabs();

	// Check if element should show text content editor
	const shouldShowTextContentEditor = () => {
		const editableTypes = [
			"text", "header", "subheader", "price",
			"invoice_number", "table_number", "customer_name",
			"date_time", "cashier", "waiter", "subtotal",
			"cgst", "sgst", "total"
		];

		return editableTypes.includes(element.type);
	};

	return (
		<div className={`${collapsedSections.elementProperties ? 'w-12' : 'w-80'} bg-white border-r border-gray-200 overflow-y-auto shadow-lg h-full flex flex-col transition-all duration-300`}>
			{collapsedSections.elementProperties ? (
				<div className="p-4 flex flex-col items-center">
					<button
						onClick={() => toggleSection('elementProperties')}
						className="p-2 hover:bg-gray-100 rounded-full"
						title="Expand Element Properties"
					>
						<svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
					</button>
					<span className="text-xs text-gray-500 mt-2 text-center">Element Properties</span>
				</div>
			) : (
				<div className="p-2 px-2 h-full flex flex-col">
					<div className="flex justify-between items-center">
						<h3 className="text-lg font-semibold text-gray-800">Element Properties</h3>
						<div className="flex items-center gap-2">
							<span
								className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded capitalize cursor-default transition-all duration-200 hover:cursor-pointer"
								onMouseEnter={() => setIsTypeHovered(true)}
								onMouseLeave={() => setIsTypeHovered(false)}
							>
								{isTypeHovered || element.type.length <= 4 ? element.type : `${element.type.slice(0, 4)}..`}
							</span>
							<button
								onClick={() => toggleSection('elementProperties')}
								className="p-1 hover:bg-gray-100 rounded"
								title="Minimize"
							>
								<svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
								</svg>
							</button>
						</div>
					</div>

					{/* Rest of the element properties content */}
					<div className="flex border-b border-gray-200 mb-4">
						{tabs.map(tab => (
							<button
								key={tab}
								onClick={() => setActiveTab(tab)}
								className={`flex-1 py-2 text-sm font-medium transition-colors ${activeTab === tab
									? "text-blue-600 border-b-2 border-blue-600"
									: "text-gray-500 hover:text-gray-700"}`}
							>
								{tab.charAt(0).toUpperCase() + tab.slice(1)}
							</button>
						))}
					</div>
					<div className="flex-1 overflow-y-auto py-">
						{activeTab === "layout" && (
							<div className="space-y-4">
								{shouldShowTextContentEditor() && (
									<div>
										<div className="flex justify-between items-center mb-2">
											<label className="block text-sm font-semibold text-gray-700">Text Content</label>
											<button
												type="button"
												onClick={() => setShowPreview(!showPreview)}
												className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
											>
												{showPreview ? 'Edit Mode' : 'Show Preview'}
											</button>
										</div>

										{showPreview ? (
											<div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm">
												<div className="font-medium text-gray-700 mb-1">Preview:</div>
												<div className="text-gray-600 whitespace-pre-wrap">
													{getPreviewText(element.content)}
												</div>
												<div className="mt-2 text-xs text-gray-500">
													This shows how the text will look with real data
												</div>
											</div>
										) : (
											<>
												<textarea
													value={element.content}
													onChange={(e) => onUpdate(element.id, { content: e.target.value })}
													className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
													rows="3"
													placeholder="Enter text or use placeholders like {{invoiceNumber}}"
												/>
												<div className="mt-1 text-xs text-gray-500">
													Use placeholders: &#123;&#123; invoiceNumber &#125;&#125;, &#123;&#123; customerName &#125;&#125;, &#123;&#123; tableNumber &#125;&#125;, etc.										</div>
											</>
										)}
									</div>
								)}


								{element.type === "image" && (
									<div>
										<label className="block text-sm font-semibold text-gray-700 mb-2">Image Source</label>
										<div className="space-y-2">
											<div className="flex gap-2">
												<button
													onClick={() => document.getElementById('image-upload').click()}
													className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 text-sm"
												>
													Upload Image
												</button>
												<input
													id="image-upload"
													type="file"
													accept="image/*"
													onChange={handleImage}
													className="hidden"
												/>
											</div>

											{element.content && (
												<div className="mt-2 p-2 border border-gray-200 rounded bg-gray-50">
													<div className="text-xs text-gray-500 mb-1">Current Image Preview:</div>
													<div className="w-full h-32 overflow-hidden rounded">
														<img
															src={element.content}
															alt="Current"
															className="w-full h-full object-contain"
															onError={(e) => {
																e.target.src = "https://via.placeholder.com/150x150?text=Image";
															}}
														/>
													</div>
												</div>
											)}
										</div>
									</div>
								)}

								{element.type === "line" && (
									<div>
										<label className="block text-sm font-semibold text-gray-700 mb-2">Line Properties</label>
										<div className="space-y-3">
											<div>
												<label className="block text-xs text-gray-600 mb-1">Line Style</label>
												<div className="flex gap-2">
													{['solid', 'dashed', 'dotted'].map(style => {
														const isActive = element.styles?.borderTop?.includes(style) ||
															(!element.styles?.borderTop && style === 'solid');
														return (
															<button
																key={style}
																onClick={() => handleLineStyleChange(style)}
																className={`flex-1 py-2 border rounded text-xs capitalize ${isActive
																	? 'border-blue-500 bg-blue-50 text-blue-700'
																	: 'border-gray-300 hover:border-gray-400'}`}
															>
																{style}
															</button>
														);
													})}
												</div>
											</div>

											<div>
												<label className="block text-xs text-gray-600 mb-1">Line Color</label>
												<div className="flex items-center gap-2">
													<input
														type="color"
														value={getCurrentLineColor()}
														onChange={(e) => handleLineColorChange(e.target.value)}
														className="w-10 h-10 rounded cursor-pointer border border-gray-300"
													/>
													<span className="text-xs font-mono">
														{getCurrentLineColor().toUpperCase()}
													</span>
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Special elements info */}
								{(element.type === "items_table" || element.type === "gst_summary") && (
									<div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
										<div className="flex items-center gap-2 mb-2">
											<svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
											<span className="text-sm font-semibold text-yellow-700">
												{element.type === "items_table" ? "Items Table" : "GST Summary"}
											</span>
										</div>
										<p className="text-xs text-yellow-600">
											This is a dynamic element. It will automatically display items and totals from the bill data.
										</p>
									</div>
								)}

								<div className="pt-4 border-t border-gray-200">
									<h4 className="text-sm font-semibold text-gray-700 mb-3">Position & Size</h4>

									<div className="grid grid-cols-2 gap-3">
										<div>
											<label className="block text-xs text-gray-600 mb-1">X Position</label>
											<input
												type="number"
												value={element.x}
												onChange={(e) => onUpdate(element.id, { x: +e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
											/>
										</div>
										<div>
											<label className="block text-xs text-gray-600 mb-1">Y Position</label>
											<input
												type="number"
												value={element.y}
												onChange={(e) => onUpdate(element.id, { y: +e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
											/>
										</div>
									</div>

									<div className="mt-3 grid grid-cols-2 gap-3">
										<div>
											<label className="block text-xs text-gray-600 mb-1">Width</label>
											<div className="relative">
												<input
													type="number"
													value={element.width}
													onChange={(e) => onResize(element.id, +e.target.value || 100, element.height)}
													min={minWidthPixels}
													className="w-full pl-0 pr-7 py-2 border border-gray-300 rounded text-sm"
												/>
												<span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
													px
												</span>
											</div>
										</div>
										<div>
											<label className="block text-xs text-gray-600 mb-1">Height</label>
											<div className="relative">
												<input
													type="number"
													value={element.height}
													onChange={(e) => onResize(element.id, element.width, +e.target.value || 40)}
													min="20"
													className="w-full pl-0 pr-7 py-2 border border-gray-300 rounded text-sm"
												/>
												<span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
													px
												</span>
											</div>
										</div>
									</div>

									<div className="py-2 text-xs text-gray-500 flex items-center justify-between">
										<span>In inches:</span>
										<span className="font-mono">
											{pixelsToInches(element.width)}" × {pixelsToInches(element.height)}"
										</span>
									</div>
								</div>
							</div>
						)}

						{/* Rest of the component remains the same... */}
						{activeTab === "text" && (
							<div className="space-y-4">
								{/* Text styling controls - same as before */}
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Font Size</label>
									<div className="flex items-center gap-2">
										<select
											value={element.styles?.fontSize || "8px"}
											onChange={(e) => handleStyle("fontSize", e.target.value)}
											className="flex-1 px-3 py-2 border border-gray-300 rounded"
										>
											{[8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72].map((s) => (
												<option key={s} value={`${s}px`}>
													{s}px
												</option>
											))}
										</select>
										<span className="text-xs text-gray-500 min-w-[30px]">
											{(element.styles?.fontSize || "8px").replace('px', '')}
										</span>
									</div>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Font Weight</label>
									<div className="grid grid-cols-3 gap-2">
										{["400", "500", "600", "700", "800", "900"].map((weight) => {
											const isActive = element.styles?.fontWeight === weight ||
												(!element.styles?.fontWeight && weight === "400");
											return (
												<button
													key={weight}
													onClick={() => handleStyle("fontWeight", weight)}
													className={`py-2 border rounded text-xs ${isActive
														? 'border-blue-500 bg-blue-50 text-blue-700 font-bold'
														: 'border-gray-300 hover:border-gray-400'}`}
													style={{ fontWeight: weight }}
												>
													{weight === "400" ? "Regular" : weight === "500" ? "Medium" :
														weight === "600" ? "SemiBold" : weight === "700" ? "Bold" :
															weight === "800" ? "ExtraBold" : "Black"}
												</button>
											);
										})}
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Text Alignment</label>
									<div className="flex border border-gray-300 rounded overflow-hidden">
										{["left", "center", "right"].map((align) => {
											const currentAlign = getTextAlignValue();
											const isActive = currentAlign === align;
											return (
												<button
													key={align}
													onClick={() => handleStyle("textAlign", align)}
													className={`flex-1 py-2 flex items-center justify-center ${isActive
														? 'bg-blue-50 text-blue-700'
														: 'hover:bg-gray-50'}`}
													title={`Align ${align}`}
												>
													<span className={`text-lg ${align === "left" ? "text-left" :
														align === "center" ? "text-center" : "text-right"} block w-6`}>
														{align === "left" ? "⎸" : align === "center" ? "⎹" : "⎹"}
													</span>
												</button>
											);
										})}
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Text Color</label>
									<div className="flex items-center gap-3">
										<input
											type="color"
											value={element.styles?.color || "#000000"}
											onChange={(e) => handleStyle("color", e.target.value)}
											className="w-12 h-12 rounded cursor-pointer border border-gray-300"
										/>
										<div className="flex-1">
											<div className="text-xs text-gray-500 mb-1">Color Code</div>
											<div className="font-mono text-sm bg-gray-100 px-3 py-2 rounded border">
												{(element.styles?.color || "#000000").toUpperCase()}
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{activeTab === "style" && (
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Font Family</label>
									<select
										value={element.styles?.fontFamily || "Arial, sans-serif"}
										onChange={(e) => handleStyle("fontFamily", e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded"
									>
										<option value="Arial, sans-serif">Arial</option>
										<option value="Helvetica, sans-serif">Helvetica</option>
										<option value="Georgia, serif">Georgia</option>
										<option value="'Times New Roman', serif">Times New Roman</option>
										<option value="'Courier New', monospace">Courier New</option>
										<option value="Verdana, sans-serif">Verdana</option>
										<option value="Tahoma, sans-serif">Tahoma</option>
										<option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
									</select>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Text Transform</label>
									<div className="grid grid-cols-3 gap-2">
										{["none", "uppercase", "capitalize", "lowercase"].map((transform) => {
											const currentTransform = getTextTransformValue();
											const isActive = currentTransform === transform;
											return (
												<button
													key={transform}
													onClick={() => handleStyle("textTransform", transform)}
													className={`py-2 border rounded text-xs capitalize ${isActive
														? 'border-blue-500 bg-blue-50 text-blue-700'
														: 'border-gray-300 hover:border-gray-400'}`}
													style={{ textTransform: transform }}
												>
													{transform}
												</button>
											);
										})}
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Font Style</label>
									<div className="grid grid-cols-2 gap-2">
										{["normal", "italic"].map((style) => {
											const currentFontStyle = getFontStyleValue();
											const isActive = currentFontStyle === style;
											return (
												<button
													key={style}
													onClick={() => handleStyle("fontStyle", style)}
													className={`py-2 border rounded text-xs ${isActive
														? 'border-blue-500 bg-blue-50 text-blue-700'
														: 'border-gray-300 hover:border-gray-400'}`}
													style={{ fontStyle: style }}
												>
													{style.charAt(0).toUpperCase() + style.slice(1)}
												</button>
											);
										})}
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Letter Spacing</label>
									<div className="flex items-center gap-3">
										<input
											type="range"
											min="0"
											max="10"
											step="0.1"
											value={parseFloat(element.styles?.letterSpacing) || 0}
											onChange={(e) => handleStyle("letterSpacing", `${e.target.value}px`)}
											className="flex-1"
										/>
										<span className="text-xs text-gray-500 min-w-[40px] text-right">
											{parseFloat(element.styles?.letterSpacing) || 0}px
										</span>
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Line Height</label>
									<div className="flex items-center gap-3">
										<input
											type="range"
											min="1"
											max="3"
											step="0.1"
											value={parseFloat(element.styles?.lineHeight) || 1.4}
											onChange={(e) => handleStyle("lineHeight", e.target.value)}
											className="flex-1"
										/>
										<span className="text-xs text-gray-500 min-w-[40px] text-right">
											{parseFloat(element.styles?.lineHeight) || 1.4}
										</span>
									</div>
								</div>
							</div>
						)}

						{activeTab === "image" && element.type === "image" && (
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Image Display</label>
									<select
										value={element.styles?.objectFit || "contain"}
										onChange={(e) => handleStyle("objectFit", e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded"
									>
										<option value="contain">Fit (contain)</option>
										<option value="cover">Fill (cover)</option>
										<option value="fill">Stretch (fill)</option>
									</select>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Border Radius</label>
									<div className="flex items-center gap-3">
										<input
											type="range"
											min="0"
											max="100"
											step="1"
											value={parseInt(element.styles?.borderRadius) || 0}
											onChange={(e) => handleStyle("borderRadius", `${e.target.value}px`)}
											className="flex-1"
										/>
										<span className="text-xs text-gray-500 min-w-[40px] text-right">
											{parseInt(element.styles?.borderRadius) || 0}px
										</span>
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Image Opacity</label>
									<div className="flex items-center gap-3">
										<input
											type="range"
											min="0"
											max="100"
											step="1"
											value={parseInt((element.styles?.opacity || 1) * 100)}
											onChange={(e) => handleStyle("opacity", `${e.target.value / 100}`)}
											className="flex-1"
										/>
										<span className="text-xs text-gray-500 min-w-[40px] text-right">
											{parseInt((element.styles?.opacity || 1) * 100)}%
										</span>
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Border</label>
									<div className="space-y-2">
										<div className="flex gap-2">
											<input
												type="color"
												value={element.styles?.borderColor || "#cccccc"}
												onChange={(e) => handleStyle("borderColor", e.target.value)}
												className="w-10 h-10 rounded cursor-pointer border border-gray-300"
											/>
											<input
												type="number"
												min="0"
												max="10"
												value={parseInt(element.styles?.borderWidth) || 0}
												onChange={(e) => handleStyle("borderWidth", `${e.target.value}px`)}
												className="flex-1 px-3 py-2 border border-gray-300 rounded"
												placeholder="Border width"
											/>
										</div>
									</div>
								</div>
							</div>
						)}

						{activeTab === "line" && element.type === "line" && (
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Line Thickness</label>
									<div className="flex items-center gap-3">
										<input
											type="range"
											min="1"
											max="10"
											step="1"
											value={parseInt(element.styles?.borderTop?.match(/\d+/)?.[0]) || 2}
											onChange={(e) => {
												const currentStyle = element.styles?.borderTop || "2px solid #000000";
												const match = currentStyle.match(/(solid|dashed|dotted)/);
												const styleType = match ? match[0] : "solid";
												const color = getCurrentLineColor();
												handleStyle("borderTop", `${e.target.value}px ${styleType} ${color}`);
											}}
											className="flex-1"
										/>
										<span className="text-xs text-gray-500 min-w-[40px] text-right">
											{parseInt(element.styles?.borderTop?.match(/\d+/)?.[0]) || 2}px
										</span>
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Line Style</label>
									<div className="grid grid-cols-3 gap-2">
										{[
											{ value: "solid", label: "Solid" },
											{ value: "dashed", label: "Dashed" },
											{ value: "dotted", label: "Dotted" },
											{ value: "double", label: "Double" },
											{ value: "groove", label: "Groove" },
											{ value: "ridge", label: "Ridge" }
										].map((style) => {
											const isActive = element.styles?.borderTop?.includes(style.value) ||
												(!element.styles?.borderTop && style.value === "solid");
											return (
												<button
													key={style.value}
													onClick={() => handleLineStyleChange(style.value)}
													className={`py-2 border rounded text-xs ${isActive
														? 'border-blue-500 bg-blue-50 text-blue-700'
														: 'border-gray-300 hover:border-gray-400'}`}
													style={{
														borderTop: `2px ${style.value} ${isActive ? '#3b82f6' : '#ccc'}`,
														borderBottom: 'none',
														borderLeft: 'none',
														borderRight: 'none'
													}}
												>
													{style.label}
												</button>
											);
										})}
									</div>
								</div>

								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">Line Color</label>
									<div className="flex items-center gap-3">
										<input
											type="color"
											value={getCurrentLineColor()}
											onChange={(e) => handleLineColorChange(e.target.value)}
											className="w-12 h-12 rounded cursor-pointer border border-gray-300"
										/>
										<div className="flex-1">
											<div className="text-xs text-gray-500 mb-1">Color Code</div>
											<div className="font-mono text-sm bg-gray-100 px-3 py-2 rounded border">
												{getCurrentLineColor().toUpperCase()}
											</div>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="border-t border-gray-200">
						<button
							onClick={() => onDelete(element.id)}
							className="w-full py-2 font-semibold text-white bg-red-500 rounded hover:bg-red-600 transition flex items-center justify-center gap-2"
						>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
							</svg>
							Delete Element
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default BillDesigner;
